// @flow

import React from 'react';
import type { Node } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { Section, Wrapper } from '@riseart/layout';
import { MediaQuery, ScrollToTopOnMount } from '@riseart/common';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { selectUnitSystem } from 'shared_services/redux/selectors/unitSystem';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import { VerticalMenuOverlay } from 'shared_components/common/menu/overlay/Menu';
import { ArtPaginatedGallerySelection } from 'shared_components/arts/gallery/PaginatedGallerySelection';
import { ArtistArtCarousel } from 'shared_components/artist/sections/art/details/Carousel';
import { ArtistArtDetails } from 'shared_components/artist/sections/art/details/Details';

import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';
import {
  artistArtSectionCls,
  artistArtSectionHeaderCls,
} from 'shared_components/artist/art/gallery/styles.less';

type Props = {
  loading: boolean,
  items: Object[],
  pagination: Object,
  viewSwitcher?: Node,
  domain: string,
  match: Object,
  location: Object,
  history: Object,
  paginationType: string,
  currentLocale: Object,
  urlParams: Object,
  artistMenuItems: Array<Object>,
  name: string,
  artistUrl: string,
  selectedArtDetailsId: number,
};

/**
 * ArtistArtGallery
 */
export const ArtistArtGallery = (props: Props) => {
  const {
    loading,
    items,
    pagination,
    location,
    history,
    currentLocale,
    artistMenuItems,
    name,
    artistUrl,
    selectedArtDetailsId,
    viewSwitcher = null,
  } = props;
  const itemsLength = items && items.length;
  const storeCode = useStoreCode();
  const { unitSystem } = useSelector(selectUnitSystem, shallowEqual);

  if (selectedArtDetailsId && !itemsLength && loading) {
    return <IsomorphicRipple isActive isFullScreen />;
  }

  return selectedArtDetailsId && itemsLength ? (
    <Section className={artistArtSectionCls}>
      <Wrapper>
        <div className={artistArtSectionHeaderCls}>
          <MediaQuery minWidth={raScreenLg}>
            {(isScreenLg) =>
              !isScreenLg ? <VerticalMenuOverlay title={name} items={artistMenuItems} /> : null
            }
          </MediaQuery>
          {viewSwitcher}
        </div>
        <ScrollToTopOnMount />
        <ArtPaginatedGallerySelection
          loading={loading}
          location={location}
          items={items}
          selectedArtDetailsId={selectedArtDetailsId}
        >
          {({ itemIdx, selectedItem, itemPositionInPage }) => (
            <ArtistArtDetails
              art={selectedItem}
              locale={currentLocale}
              storeCode={storeCode}
              unitSystem={unitSystem}
              artistName={name}
              artistUrl={artistUrl}
              menuItems={artistMenuItems}
              positionLabel={`${
                (pagination.currentPage - 1) * pagination.itemsPerPage + itemPositionInPage
              } / ${pagination.totalItems}`}
            >
              <IsomorphicRipple isActive={loading}>
                <ArtistArtCarousel
                  items={items}
                  pagination={pagination}
                  itemIdx={itemIdx}
                  history={history}
                  location={location}
                />
              </IsomorphicRipple>
            </ArtistArtDetails>
          )}
        </ArtPaginatedGallerySelection>
      </Wrapper>
    </Section>
  ) : null;
};
